<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import imgGirlWithHeadset from '@/assets/girl-with-headset.png';
import imgHotGirlWithAHeadset from '@/assets/hot-girl-with-a-headset.png';
import imgCatWearingAGamingHeadset from '@/assets/cat-wearing-a-gaming-headset.png';
import imgDogWithHeadset from '@/assets/dog-with-headset.png';
import imgPersonWithHeadset from '@/assets/person-with-headset.png';
import imgPersonWithHeadset1 from '@/assets/person-with-headset (1).png';
import imgPodcasterWithMic from '@/assets/podcaster-with-mic.png';

const activeSpeaker = ref(0);
const currentParticipants = ref<number[]>([0, 4, 2, 3]);
const leavingParticipant = ref<number | null>(null);
const joiningParticipant = ref<number | null>(null);

const allPersonas = [
  { image: imgGirlWithHeadset, alt: 'Girl speaking', name: 'Sarah' },
  {
    image: imgHotGirlWithAHeadset,
    alt: 'Language enthusiast speaking',
    name: 'Emma',
  },
  { image: imgCatWearingAGamingHeadset, alt: 'Cat speaking', name: 'Whiskers' },
  { image: imgDogWithHeadset, alt: 'Dog speaking', name: 'Buddy' },
  {
    image: imgPersonWithHeadset,
    alt: 'Professional speaking',
    name: 'Michael',
  },
  { image: imgPersonWithHeadset1, alt: 'Student speaking', name: 'Chris' },
  { image: imgPodcasterWithMic, alt: 'Podcaster speaking', name: 'Alex' },
] as const;

const getPersona = (index: number) => {
  const persona = allPersonas[index];
  return persona || allPersonas[0];
};

onMounted(() => {
  // Rotate active speaker every 3-10 seconds
  const speakerInterval = setInterval(
    () => {
      if (currentParticipants.value.length === 0) return;

      const randomIndex = Math.floor(
        Math.random() * currentParticipants.value.length,
      );
      activeSpeaker.value = currentParticipants.value[randomIndex] ?? 0;
    },
    Math.floor(Math.random() * 7000) + 3000,
  );

  // Someone joins or leaves every 10 seconds
  const participantInterval = setInterval(() => {
    if (joiningParticipant.value !== null || leavingParticipant.value !== null)
      return;

    const shouldAdd = Math.random() > 0.5;

    if (shouldAdd && currentParticipants.value.length < 6) {
      const availableToJoin = Array.from(
        { length: allPersonas.length },
        (_, i) => i,
      ).filter((i) => !currentParticipants.value.includes(i));

      if (availableToJoin.length > 0) {
        const newParticipant =
          availableToJoin[Math.floor(Math.random() * availableToJoin.length)];
        if (typeof newParticipant === 'number' && newParticipant >= 0) {
          joiningParticipant.value = newParticipant;
          currentParticipants.value.push(newParticipant);
          setTimeout(() => (joiningParticipant.value = null), 1000);
        }
      }
    } else if (currentParticipants.value.length > 3) {
      const availableToLeave = currentParticipants.value.filter(
        (p) => p !== activeSpeaker.value,
      );

      if (availableToLeave.length > 0) {
        const participantToRemove =
          availableToLeave[Math.floor(Math.random() * availableToLeave.length)];
        if (
          typeof participantToRemove === 'number' &&
          participantToRemove >= 0
        ) {
          leavingParticipant.value = participantToRemove;
          currentParticipants.value = currentParticipants.value.filter(
            (p) => p !== participantToRemove,
          );
          setTimeout(() => (leavingParticipant.value = null), 1000);
        }
      }
    }
  }, 10000);

  onUnmounted(() => {
    clearInterval(speakerInterval);
    clearInterval(participantInterval);
  });
});
</script>

<template>
  <div class="relative">
    <!-- Chat window frame -->
    <div
      class="relative bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden"
    >
      <!-- Header -->
      <div class="bg-accent/90 backdrop-blur-sm p-4 flex items-center gap-3">
        <div class="w-3 h-3 rounded-full bg-red-400" />
        <div class="w-3 h-3 rounded-full bg-yellow-400" />
        <div class="w-3 h-3 rounded-full bg-green-400" />
      </div>

      <!-- Chat content -->
      <div class="p-6 space-y-4">
        <!-- Active speaker with animation -->
        <div class="relative h-48 flex justify-center">
          <div
            v-for="personaIndex in currentParticipants"
            :key="getPersona(personaIndex).image"
            :class="[
              'absolute transition-all duration-500',
              personaIndex === activeSpeaker
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 translate-y-4 scale-95',
              personaIndex === leavingParticipant ? 'animate-fade-out' : '',
              personaIndex === joiningParticipant ? 'animate-fade-in' : '',
            ]"
          >
            <img
              :src="getPersona(personaIndex).image"
              :alt="getPersona(personaIndex).alt"
              class="w-48 h-48 object-contain"
            />
            <!-- Speaking indicator -->
            <div
              v-if="personaIndex === activeSpeaker"
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10"
            >
              <div
                class="w-52 h-52 border-2 border-accent/20 rounded-full animate-ping-slow"
              />
              <div
                class="absolute inset-0 w-52 h-52 border-2 border-accent/10 rounded-full animate-ping-slower"
              />
            </div>
          </div>
        </div>

        <!-- Participant thumbnails -->
        <div class="flex justify-center gap-4 mt-6">
          <div
            v-for="personaIndex in currentParticipants"
            :key="getPersona(personaIndex).image"
            :class="[
              'relative cursor-pointer transition-all duration-500',
              personaIndex === activeSpeaker
                ? 'grayscale-0 opacity-100 scale-110'
                : 'grayscale opacity-50 scale-100',
              'hover:grayscale-0 hover:opacity-100',
              personaIndex === leavingParticipant ? 'animate-fade-out' : '',
              personaIndex === joiningParticipant ? 'animate-fade-in' : '',
            ]"
            @click="activeSpeaker = personaIndex"
          >
            <img
              :src="getPersona(personaIndex).image"
              :alt="getPersona(personaIndex).alt"
              class="w-20 h-20 object-contain"
            />
            <div
              class="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full text-xs font-medium text-gray-600"
            >
              {{ getPersona(personaIndex).name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
